/**
 * ALTIORA Design System — Live Gallery
 *
 * Route: /app/design-system
 *
 * Every primitive of the ALTIORA Design System is documented and
 * showcased here so designers, engineers and QA can visually verify
 * that a change to the tokens/motion/primitives propagates correctly
 * across the product.
 *
 * This page IS the visual regression baseline. Screenshots taken by
 * the automated visual test hit this page in mobile / tablet / desktop.
 */
import { useState } from "react";
import {
  Sparkles, Search, Bell, Heart, Trash2, Download, Upload,
  Settings, Plus, ExternalLink, User, Palette, MousePointerClick,
  KeyRound, Waves, Layers, ListChecks, BookOpen, Compass,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, Section, Pill, PrimaryButton, GhostButton,
  TabsShell, Metric, Divider, Loader, Skeleton, Badge, EmptyState,
  Alert, Label, FormField, Input, Textarea, Select, Checkbox,
  Modal, Table, Accordion, Tooltip, DangerButton, IconButton,
  Breadcrumbs, StatCard,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import { ANIMATION_DIRECTORY } from "@/components/altiora/motion";
import { TopBar } from "@/components/layout/TopBar";

/* ──────────────────────────────────────────────────────────────────
   Small helper — labelled "swatch" block used inside sections
   ────────────────────────────────────────────────────────────────── */
const Swatch = ({ label, color, textColor = "#fff" }) => (
  <div className="rounded-lg overflow-hidden text-[11px] font-semibold"
          style={{ background: color, color: textColor }}
          data-testid={`ds-swatch-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="h-16" />
    <div className="p-2 flex items-center justify-between"
            style={{ background: "rgba(0,0,0,0.15)" }}>
      <span>{label}</span><span>{color}</span>
    </div>
  </div>
);

const DocRow = ({ name, prop, description, ex }) => (
  <tr>
    <td className="py-1.5 pr-3" style={{ color: NAVY, fontWeight: 600 }}>{name}</td>
    <td className="py-1.5 pr-3 font-mono text-[11px]" style={{ color: CHAMPAGNE }}>{prop}</td>
    <td className="py-1.5 pr-3" style={{ color: SLATE }}>{description}</td>
    <td className="py-1.5" style={{ color: SLATE, fontStyle: "italic" }}>{ex}</td>
  </tr>
);

/* ──────────────────────────────────────────────────────────────────
   MAIN
   ────────────────────────────────────────────────────────────────── */
export default function DesignSystem() {
  const [tab, setTab] = useState("foundations");
  const [modalOpen, setModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(true);
  const [check, setCheck] = useState(false);

  const tabs = [
    { id: "foundations", label: "Fondations",    icon: Palette },
    { id: "buttons",     label: "Boutons",       icon: MousePointerClick },
    { id: "forms",       label: "Formulaires",   icon: KeyRound },
    { id: "feedback",    label: "Feedback",      icon: Bell },
    { id: "layout",      label: "Structure",     icon: Layers },
    { id: "data",        label: "Données",       icon: ListChecks },
    { id: "motion",      label: "Animations",    icon: Waves },
    { id: "usage",       label: "Utilisation",   icon: BookOpen },
  ];

  return (
    <PageShell testid="ds-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <Breadcrumbs items={[
          { label: "ALTIORA", href: "/" },
          { label: "Design System" },
        ]} testid="ds-breadcrumbs" />
        <div className="mt-3" />
        <PageHeader
          eyebrow="ALTIORA · Design System v1"
          title="Le langage visuel unique"
          subtitle="La référence officielle : tokens, animations, primitives accessibles. Toute nouvelle interface doit s'appuyer sur cette bibliothèque — jamais sur du CSS ad-hoc."
          testid="ds-header" />

        <TabsShell tabs={tabs} active={tab} onChange={setTab} testid="ds-tab" />

        {/* ───── FOUNDATIONS ───────────────────────────────────── */}
        {tab === "foundations" && (
          <div className="space-y-6" data-testid="ds-panel-foundations">
            <Card className="p-6">
              <Section icon={Palette} title="Palette de couleurs"
                          hint="Ne jamais utiliser d'autre code hexadécimal ailleurs dans le produit.">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                  <Swatch label="Navy"      color="#0B2545" />
                  <Swatch label="Navy Dark" color="#061528" />
                  <Swatch label="Champagne" color="#C5A880" textColor="#061528" />
                  <Swatch label="Gold"      color="#F5D687" textColor="#061528" />
                  <Swatch label="Ivory"     color="#FBF6EA" textColor="#0B2545" />
                  <Swatch label="Slate"     color="#94A3B8" />
                  <Swatch label="Green"     color="#0B7A3B" />
                  <Swatch label="Red"       color="#B91C1C" />
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Typographie" icon={BookOpen}
                          hint="La typo « Playfair Display » (serif) est réservée aux titres. Le corps utilise la stack système.">
                <div className="mt-3 space-y-3">
                  <div className="font-serif text-4xl" style={{ color: NAVY }}>
                    H1 · Grand titre de section
                  </div>
                  <div className="font-serif text-2xl" style={{ color: NAVY }}>
                    H2 · Titre de page
                  </div>
                  <div className="font-serif text-xl" style={{ color: NAVY }}>
                    H3 · Sous-section
                  </div>
                  <div className="text-[15px]" style={{ color: NAVY }}>
                    Corps du texte — Lorem ipsum dolor sit amet, consectetur
                    adipiscing elit. La lisibilité prime sur la densité.
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: CHAMPAGNE }}>
                    Overline · signature ALTIORA
                  </div>
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Espacements & Radii" hint="Multiples de 4px · Radii de 4 / 8 / 12 / 16 / 20 px.">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                  {[4, 8, 12, 16, 20].map(px => (
                    <div key={px} className="text-center">
                      <div className="mx-auto"
                              style={{ width: 56, height: 56, borderRadius: px,
                                          background: CHAMPAGNE }} />
                      <div className="text-[10px] mt-1" style={{ color: SLATE }}>
                        radius {px}px
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            </Card>

            <Alert variant="info" title="Focus visible"
                       testid="ds-alert-focus">
              Chaque élément interactif reçoit un anneau de focus 2px navy en
              tab. Testez avec ⇥ (Tab) pour vérifier.
            </Alert>
          </div>
        )}

        {/* ───── BUTTONS ───────────────────────────────────────── */}
        {tab === "buttons" && (
          <div className="space-y-6" data-testid="ds-panel-buttons">
            <Card className="p-6">
              <Section title="Famille de boutons"
                          hint="Un seul PrimaryButton par vue — hiérarchie visuelle.">
                <div className="flex flex-wrap gap-3 mt-3">
                  <PrimaryButton testid="ds-primary">
                    <Sparkles size={13} /> Action principale
                  </PrimaryButton>
                  <GhostButton testid="ds-ghost">
                    <Plus size={13} /> Action secondaire
                  </GhostButton>
                  <DangerButton testid="ds-danger">
                    <Trash2 size={13} /> Supprimer
                  </DangerButton>
                  <PrimaryButton disabled testid="ds-primary-disabled">Désactivé</PrimaryButton>
                </div>
                <Divider />
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: CHAMPAGNE }}>
                  IconButton (tone: neutral · solid · ghost · gold)
                </div>
                <div className="flex gap-2">
                  <IconButton icon={Bell}     label="Notifications" tone="neutral" testid="ds-ib-neutral" />
                  <IconButton icon={Settings} label="Réglages"      tone="solid"   testid="ds-ib-solid" />
                  <IconButton icon={Heart}    label="Aimer"         tone="ghost"   testid="ds-ib-ghost" />
                  <IconButton icon={Download} label="Télécharger"    tone="gold"    testid="ds-ib-gold" />
                </div>
                <Divider />
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: CHAMPAGNE }}>
                  Boutons avec tooltip accessible
                </div>
                <Tooltip label="Créer un nouvel élément" testid="ds-tooltip">
                  <IconButton icon={Plus} label="Nouveau" tone="solid" />
                </Tooltip>
              </Section>
            </Card>
          </div>
        )}

        {/* ───── FORMS ─────────────────────────────────────────── */}
        {tab === "forms" && (
          <div className="space-y-6" data-testid="ds-panel-forms">
            <Card className="p-6">
              <Section title="Champs de formulaire"
                          hint="Toujours utiliser <FormField> pour bénéficier des labels, descriptions, erreurs et aria-attributes automatiques.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <FormField label="Adresse courriel" required
                                 description="Nous ne partageons jamais cette adresse."
                                 testid="ds-ff-email">
                    {(a) => <Input type="email" placeholder="vous@altiora.io"
                                          testid="ds-input-email" {...a} />}
                  </FormField>
                  <FormField label="Rôle" testid="ds-ff-role">
                    {(a) => <Select testid="ds-select-role" {...a}>
                      <option>Membre</option>
                      <option>Coach</option>
                      <option>Admin</option>
                    </Select>}
                  </FormField>
                  <FormField label="Message" hint="max 500 caractères"
                                 error="Le message est trop court."
                                 testid="ds-ff-msg" >
                    {(a) => <Textarea testid="ds-textarea" rows={4}
                                              placeholder="Votre message…" {...a} />}
                  </FormField>
                  <div className="pt-6">
                    <Checkbox label="J'accepte les conditions d'utilisation"
                                  checked={check} onChange={(e) => setCheck(e.target.checked)}
                                  testid="ds-check-terms" />
                    <div className="mt-3">
                      <PrimaryButton testid="ds-form-submit">
                        Envoyer
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              </Section>
            </Card>
          </div>
        )}

        {/* ───── FEEDBACK ─────────────────────────────────────── */}
        {tab === "feedback" && (
          <div className="space-y-4" data-testid="ds-panel-feedback">
            {alertOpen && (
              <Alert variant="success" title="Enregistré avec succès"
                         onDismiss={() => setAlertOpen(false)} testid="ds-alert-success">
                Vos modifications ont été appliquées.
              </Alert>
            )}
            <Alert variant="info"    title="Astuce" testid="ds-alert-info">
              Utilisez ⇥ pour naviguer entre les champs.
            </Alert>
            <Alert variant="warning" title="Attention" testid="ds-alert-warn">
              Cette action est réversible pendant 30 jours.
            </Alert>
            <Alert variant="danger"  title="Erreur" testid="ds-alert-danger">
              Impossible de contacter le serveur. Réessayez.
            </Alert>

            <Card className="p-6">
              <Section title="Badges" icon={Sparkles}
                          hint="Ne pas confondre Badge (statut, sémantique) et Pill (chip décoratif).">
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="neutral" testid="ds-badge-neutral">Neutre</Badge>
                  <Badge variant="navy"    testid="ds-badge-navy">Navy</Badge>
                  <Badge variant="gold"    testid="ds-badge-gold">Premium</Badge>
                  <Badge variant="green"   testid="ds-badge-green">Actif</Badge>
                  <Badge variant="amber"   testid="ds-badge-amber">En attente</Badge>
                  <Badge variant="red"     testid="ds-badge-red">Erreur</Badge>
                  <Badge variant="info"    testid="ds-badge-info">Info</Badge>
                  <Badge variant="solid"   testid="ds-badge-solid">Solide</Badge>
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Empty State" testid="ds-empty">
                <EmptyState icon={Search} title="Aucun résultat"
                                description="Essayez d'ajuster vos filtres ou d'élargir votre recherche."
                                action={<PrimaryButton>Réinitialiser</PrimaryButton>}
                                testid="ds-empty-state" />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Skeleton loaders">
                <div className="space-y-2 mt-3" data-testid="ds-skeletons">
                  <Skeleton height={16} width="60%" />
                  <Skeleton height={12} width="90%" />
                  <Skeleton height={12} width="80%" />
                  <Skeleton height={40} width="100%" rounded="lg" />
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Loader">
                <div className="flex items-center gap-4 mt-3">
                  <Loader size={16} testid="ds-loader-sm" /> Petit
                  <Loader size={24} testid="ds-loader-md" /> Moyen
                  <Loader size={32} testid="ds-loader-lg" /> Grand
                </div>
              </Section>
            </Card>
          </div>
        )}

        {/* ───── LAYOUT ────────────────────────────────────────── */}
        {tab === "layout" && (
          <div className="space-y-4" data-testid="ds-panel-layout">
            <Card className="p-6">
              <Section title="Metric · KPI">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <Metric label="Utilisateurs actifs" value="1 247" hint="30 j" testid="ds-metric-1" />
                  <Metric label="Revenus mensuels"     value="128k$" accent="#0B7A3B" testid="ds-metric-2" />
                  <Metric label="Taux conversion"       value="4,3 %" accent="#C5A880" testid="ds-metric-3" />
                  <Metric label="Churn"                 value="1,2 %" accent="#B91C1C" testid="ds-metric-4" />
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="StatCard · gros chiffres marketing">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <StatCard value="12" suffix="M$" label="Levés" accent="gold" testid="ds-stat-1" />
                  <StatCard value="4 200" label="Coachs accompagnés" accent="champagne" testid="ds-stat-2" />
                  <StatCard value="98" suffix="%" label="Satisfaction" accent="navy" testid="ds-stat-3" />
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Accordion" hint="Utile pour la FAQ ou l'aide contextuelle.">
                <Accordion testid="ds-accordion" items={[
                  { title: "Qu'est-ce qu'ALTIORA ?",
                    content: "ALTIORA est une Intelligence Décisionnelle conçue pour les professionnels, les organisations et les institutions. Elle centralise vos opportunités, apprend de vos décisions et propose des actions à haute valeur." },
                  { title: "Comment protégez-vous mes données ?",
                    content: "JWT + HttpOnly cookie, chiffrement au repos, audit trail complet, conforme RGPD." },
                  { title: "Puis-je exporter mes données ?",
                    content: "Oui, à tout moment depuis Paramètres → Mon compte → Exporter." },
                ]} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title="Modale · Dialog accessible"
                          hint="Focus trap · ESC pour fermer · aria-labelledby">
                <PrimaryButton onClick={() => setModalOpen(true)} testid="ds-open-modal">
                  Ouvrir la modale
                </PrimaryButton>
              </Section>
            </Card>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}
                       size="md" testid="ds-modal"
                       title="Exemple de modale accessible"
                       description="Fermez avec Échap · Tab cyclique interne · retour au bouton d'origine."
                       footer={
                         <>
                           <GhostButton onClick={() => setModalOpen(false)} testid="ds-modal-cancel">
                             Annuler
                           </GhostButton>
                           <PrimaryButton onClick={() => setModalOpen(false)} testid="ds-modal-confirm">
                             Confirmer
                           </PrimaryButton>
                         </>
                       }>
              <p className="text-[13px]" style={{ color: NAVY }}>
                Le contenu de la modale peut être n'importe quel enfant React :
                formulaires, cartes, listes… La modale reste centrée et respecte
                les préférences de mouvement réduit.
              </p>
            </Modal>
          </div>
        )}

        {/* ───── DATA ─────────────────────────────────────────── */}
        {tab === "data" && (
          <div className="space-y-4" data-testid="ds-panel-data">
            <Card className="p-6">
              <Section title="Table · lignes animées et accessibles">
                <Table testid="ds-table"
                       columns={[
                         { key: "name", label: "Nom" },
                         { key: "role", label: "Rôle",
                           render: r => <Badge variant={r.roleTone}>{r.role}</Badge> },
                         { key: "status", label: "Statut",
                           render: r => <Pill tone={r.tone}>{r.status}</Pill> },
                         { key: "actions", label: "Actions",
                           render: () => (
                             <div className="flex gap-1">
                               <IconButton icon={ExternalLink} label="Ouvrir" size={26} tone="ghost" />
                               <IconButton icon={Trash2}        label="Supprimer" size={26} tone="ghost" />
                             </div>
                           )},
                       ]}
                       rows={[
                         { id: 1, name: "Marie L.",  role: "Coach",  roleTone: "navy",  status: "Actif",     tone: "green" },
                         { id: 2, name: "Jean D.",   role: "Membre", roleTone: "gold",  status: "En attente", tone: "amber" },
                         { id: 3, name: "Sarah B.",  role: "Admin",  roleTone: "navy",  status: "Suspendu",  tone: "red" },
                       ]} />
              </Section>
            </Card>
          </div>
        )}

        {/* ───── MOTION ────────────────────────────────────────── */}
        {tab === "motion" && (
          <div className="space-y-4" data-testid="ds-panel-motion">
            <Alert variant="info" title="Prefers-reduced-motion respecté partout"
                       testid="ds-motion-note">
              Toutes les animations utilisent <code>useSafeMotion</code> et se
              désactivent automatiquement si le système de l'utilisateur préfère
              un mouvement réduit.
            </Alert>
            <Card className="p-6">
              <Section title="Bibliothèque d'animations · directory">
                <Table testid="ds-motion-table"
                       columns={[
                         { key: "id",    label: "Preset",     render: r => <code className="text-[11px]" style={{ color: CHAMPAGNE }}>{r.id}</code> },
                         { key: "fr",    label: "Description" },
                         { key: "usage", label: "Cas d'usage" },
                       ]}
                       rows={ANIMATION_DIRECTORY} />
              </Section>
            </Card>
          </div>
        )}

        {/* ───── USAGE ────────────────────────────────────────── */}
        {tab === "usage" && (
          <div className="space-y-4" data-testid="ds-panel-usage">
            <Card className="p-6">
              <Section icon={Compass} title="Comment utiliser le Design System"
                          hint="Toutes les nouvelles pages doivent respecter ces règles.">
                <ol className="list-decimal ml-5 text-[13px] space-y-2 mt-3"
                       style={{ color: NAVY }}>
                  <li>Enveloppez chaque page dans <code style={{ color: CHAMPAGNE }}>&lt;PageShell&gt;</code>.</li>
                  <li>Utilisez <code style={{ color: CHAMPAGNE }}>&lt;PageHeader&gt;</code> avec eyebrow · title · subtitle.</li>
                  <li>Groupez le contenu dans <code style={{ color: CHAMPAGNE }}>&lt;Card&gt;</code> + <code style={{ color: CHAMPAGNE }}>&lt;Section&gt;</code>.</li>
                  <li>Onglets : <code style={{ color: CHAMPAGNE }}>&lt;TabsShell&gt;</code> (jamais un menu ad-hoc).</li>
                  <li>Boutons : <code style={{ color: CHAMPAGNE }}>PrimaryButton</code> unique · <code style={{ color: CHAMPAGNE }}>GhostButton</code> pour le reste.</li>
                  <li>Formulaires : toujours <code style={{ color: CHAMPAGNE }}>FormField</code> (aria automatique).</li>
                  <li>Feedback : <code style={{ color: CHAMPAGNE }}>Alert</code>, <code style={{ color: CHAMPAGNE }}>Toast (sonner)</code>, <code style={{ color: CHAMPAGNE }}>EmptyState</code>, <code style={{ color: CHAMPAGNE }}>Skeleton</code>.</li>
                  <li>Animations : import depuis <code style={{ color: CHAMPAGNE }}>@/components/altiora/motion</code>.</li>
                </ol>
                <Divider />
                <table className="w-full text-[12px] mt-3">
                  <thead>
                    <tr style={{ color: SLATE }}>
                      <th className="text-left pb-2 pr-3 text-[10px] uppercase tracking-widest">Composant</th>
                      <th className="text-left pb-2 pr-3 text-[10px] uppercase tracking-widest">Import</th>
                      <th className="text-left pb-2 pr-3 text-[10px] uppercase tracking-widest">Usage</th>
                      <th className="text-left pb-2 text-[10px] uppercase tracking-widest">Exemple</th>
                    </tr>
                  </thead>
                  <tbody>
                    <DocRow name="PrimaryButton" prop="PremiumUI" description="Action principale" ex="'Sauvegarder'" />
                    <DocRow name="Modal"          prop="PremiumUI" description="Dialogue focus-trap" ex="'Confirmer suppression'" />
                    <DocRow name="Table"          prop="PremiumUI" description="Liste tabulaire animée" ex="Utilisateurs, opportunités" />
                    <DocRow name="Alert"          prop="PremiumUI" description="Message sémantique inline" ex="Succès enregistrement" />
                    <DocRow name="Accordion"      prop="PremiumUI" description="Zones repliables" ex="FAQ" />
                    <DocRow name="Tooltip"        prop="PremiumUI" description="Info-bulle accessible" ex="Icônes seuls" />
                    <DocRow name="FormField"      prop="PremiumUI" description="Champ avec label + error" ex="Formulaire de compte" />
                  </tbody>
                </table>
              </Section>
            </Card>

            <Card className="p-6">
              <Section icon={User} title="Accessibilité — checklist WCAG 2.2 AA">
                <ul className="mt-3 space-y-1 text-[13px]" style={{ color: NAVY }}>
                  <li>✓ Contraste texte ≥ 4,5:1 (corps) · ≥ 3:1 (grand texte)</li>
                  <li>✓ Focus visible partout (anneau navy 2px)</li>
                  <li>✓ Toutes les actions accessibles au clavier (Tab / Enter / Space)</li>
                  <li>✓ Labels explicites sur chaque input (via FormField)</li>
                  <li>✓ Zones tactiles ≥ 44×44 px en mobile</li>
                  <li>✓ Roles ARIA (dialog, alert, region, status) automatiques</li>
                  <li>✓ <code>prefers-reduced-motion</code> respecté</li>
                  <li>✓ Screen-reader labels (`sr-only-altiora`) sur les états</li>
                </ul>
              </Section>
            </Card>
          </div>
        )}
      </div>
    </PageShell>
  );
}
